import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../../utils/helper';
import InvoiceStatusFilter from '../../../components/common/InvoiceStatusFilter';
import InvoiceStatus from '../../../components/common/InvoiceStatus';
import InvoiceDetails from '../../../components/common/InvoiceDetails';
import InvoiceActions from '../../../components/common/InvoiceActions';
import api from '../../../services/api';


const InvoiceList = ({ 
  showSuccessMessage, 
  invoices = [], 
  invoiceStatusOptions = [],
  onViewInvoice,
  onSendInvoice,
  onDownloadInvoice,
  onAddPayment,
  onCancelInvoice,
  onMarkPaidInvoice
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Invoice details modal state - removed, handled by parent CustomerDetails

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewInvoice = (invoice) => {
    // Use parent's onViewInvoice handler
    if (onViewInvoice) {
      onViewInvoice(invoice);
    }
  };

  // Filter invoices based on search term and status
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchTerm === '' || 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Check both status and status_verbose for filtering
    const matchesStatus = selectedStatus === 'all' || 
      invoice.status === selectedStatus || 
      invoice.status_verbose === selectedStatus ||
      (invoiceStatusOptions.find(option => option.value === selectedStatus)?.label === invoice.status_verbose);
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change and ensure page is within bounds
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedStatus, invoices]);

  // Ensure page is within valid bounds when totalPages changes
  useEffect(() => {
    if (page >= totalPages && totalPages > 0) {
      setPage(totalPages - 1);
    }
  }, [totalPages, page]);

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setSelectedStatus(status);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0);
  };



    return (
    <Box sx={{ p: 4 }}>
      {/* Invoice Table */}

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Search by invoice #"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InvoiceStatusFilter
                  value={selectedStatus}
                  onChange={handleStatusFilterChange}
                  size="small"
                  fullWidth
                  label="Status"
                  showIcons={true}
                  options={invoiceStatusOptions}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <Paper
                    elevation={0}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      px: 2,
                      py: 0.75,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        mr: 1.5,
                        display: { xs: 'none', sm: 'block' }
                      }}
                    >
                      Rows per page:
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      size="small"
                      sx={{ 
                        minWidth: '65px',
                        height: '30px',
                        mr: 3,
                        '& .MuiSelect-select': {
                          py: 0.5,
                          fontWeight: 500
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'transparent'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider'
                        }
                      }}
                      onChange={handleChangeRowsPerPage}
                      variant="outlined"
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        mx: 2
                      }}
                    >
                      {filteredInvoices.length > 0 
                        ? `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredInvoices.length)} of ${filteredInvoices.length}`
                        : '0-0 of 0'
                      }
                    </Typography>
                    <Box sx={{ 
                      display: 'flex',
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      pl: 1
                    }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handlePageChange(page - 1)} 
                        disabled={page === 0}
                        sx={{ 
                          color: page === 0 ? 'text.disabled' : 'primary.main'
                        }}
                      >
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handlePageChange(page + 1)} 
                        disabled={page >= totalPages - 1}
                        sx={{ 
                          color: page >= totalPages - 1 ? 'text.disabled' : 'primary.main'
                        }}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount Due</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Shared On</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || selectedStatus !== 'all' ? 'No invoices match your search criteria.' : 'There are no invoices available.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentInvoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id} 
                      hover
                      onClick={(e) => {
                        // Don't trigger row click if clicking on actions button
                        if (e.target.closest('[data-actions-button]')) {
                          return;
                        }
                        handleViewInvoice(invoice);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ fontWeight: 600 }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              handleViewInvoice(invoice);
                            }}
                          >
                            #{invoice.invoice_number}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                          >
                            {formatDate(invoice.created_at)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {invoice.due_date}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {invoice.total_amount}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {invoice.due_amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <InvoiceStatus 
                          status={invoice.status}
                          status_verbose={invoice.status_verbose}
                          size="small"
                          showIcon={true}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {invoice.shared_on ? formatDate(invoice.shared_on) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <InvoiceActions
                          invoice={invoice}
                          onView={() => onViewInvoice && onViewInvoice(invoice)}
                          onMail={() => onSendInvoice && onSendInvoice(invoice)}
                          onDownload={() => onDownloadInvoice && onDownloadInvoice(invoice)}
                          onAddPayment={() => onAddPayment && onAddPayment(invoice)}
                          onCancel={() => onCancelInvoice && onCancelInvoice(invoice)}
                          onMarkPaid={() => onMarkPaidInvoice && onMarkPaidInvoice(invoice)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

      {/* Invoice Details Modal - Removed duplicate, handled by parent CustomerDetails */}

    </Box>
  );
};

export default InvoiceList; 